/**
 * 재질 슬롯 → 실제 three 재질.
 *
 * 나무는 world 재질(materials.ts)의 절차 텍스처를 그대로 쓴다. 벽·바닥과 같은
 * 나뭇결을 공유해야 가구가 저택에 "속해" 보인다. 금속/천 등은 색과 반사 특성만 다르다.
 */

import * as THREE from 'three';
import { surfaces } from '../materials';
import { fleshMaterials } from './flesh';
import type { Slot } from './kernel';

let cache: Map<Slot, THREE.Material> | null = null;

export function slotMaterials(renderer: THREE.WebGLRenderer): Map<Slot, THREE.Material> {
  if (cache) return cache;
  const surf = surfaces(renderer);
  const oakTex = surf.get('oak')!;
  const woodTex = surf.get('darkwood')!;

  /** 나무 슬롯: 같은 결 텍스처에 색만 달리해 수종을 만든다 */
  const wood = (base: THREE.MeshStandardMaterial, tint: string, rough: number) => {
    const m = base.clone();
    m.color = new THREE.Color(tint);
    m.roughness = rough;
    m.vertexColors = true;
    return m;
  };

  cache = new Map<Slot, THREE.Material>([
    /* 거칠기 — 0.5/0.42 는 래커 칠한 피아노처럼 번들거렸다(2026-09-15 Chris: 「월넛이면 왜케 반짝이고」). 오일 마감 새틴으로 */
    ['oak', wood(oakTex.material, '#b98f5f', 0.72)],
    ['walnut', wood(woodTex.material, '#8a6142', 0.74)],
    ['ebony', wood(woodTex.material, '#37312a', 0.66)],
    [
      'steel',
      new THREE.MeshStandardMaterial({ color: '#8f959d', roughness: 0.38, metalness: 0.82 }),
    ],
    ['brass', new THREE.MeshStandardMaterial({ color: '#c8a24e', roughness: 0.26, metalness: 0.92 })],
    ['fabric', new THREE.MeshStandardMaterial({ color: '#4d5262', roughness: 0.96, metalness: 0 })],
    ['leather', new THREE.MeshStandardMaterial({ color: '#53392a', roughness: 0.62, metalness: 0.04 })],
    // 유리도 **transmission 을 쓰지 않는다**(아래 'pane' 과 같은 이유). 병·유리문 하나만 있어도
    // three 가 프레임마다 불투명 씬을 한 번 더 그리고, 그 패스용 셰이더를 전부 한 벌 더 굽는다 —
    // 에디터 첫 로딩의 절반이 이것이었다(2026-09-25 측정: transmission 패스 안 컴파일 43초/85초, SwiftShader)
    [
      'glass',
      new THREE.MeshStandardMaterial({
        color: '#dfe7ef',
        roughness: 0.06,
        metalness: 0.1,
        transparent: true,
        opacity: 0.32,
      }),
    ],
    ['stone', new THREE.MeshStandardMaterial({ color: '#4a4d52', roughness: 0.42, metalness: 0.05 })],
    ['paper', new THREE.MeshStandardMaterial({ color: '#ded6c2', roughness: 0.95, metalness: 0 })],
    // 등갓처럼 얇은 껍질이 섞여 있어 양면으로 그린다 (닫힌 덩어리는 뒷면이 어차피 안 보인다)
    [
      'paint',
      new THREE.MeshStandardMaterial({
        color: '#cdc8be',
        roughness: 0.58,
        metalness: 0.02,
        side: THREE.DoubleSide,
      }),
    ],
    // --- 신당/무속 ---
    // 옻칠: 반사가 또렷해야 '칠한 나무'로 읽힌다
    ['lacquer', new THREE.MeshStandardMaterial({ color: '#3c1e1a', roughness: 0.22, metalness: 0.08 })],
    // 청동: 놋쇠보다 어둡고 녹빛. 향로·방울
    ['bronze', new THREE.MeshStandardMaterial({ color: '#7a6a48', roughness: 0.52, metalness: 0.78 })],
    // 한지: 빛을 머금는다 — 등롱 안에서 켜면 은은하게 살아나야 한다
    [
      'hanji',
      new THREE.MeshStandardMaterial({
        color: '#dccb9c',
        roughness: 0.92,
        metalness: 0,
        side: THREE.DoubleSide,
        emissive: new THREE.Color('#c9a45e'),
        emissiveIntensity: 0.12,
      }),
    ],
    ['redcloth', new THREE.MeshStandardMaterial({ color: '#8c2420', roughness: 0.93, metalness: 0 })],
    // 북·장구 가죽은 두께 없는 막이다 — 양면으로 그려야 뒤에서 봐도 사라지지 않는다
    [
      'hide',
      new THREE.MeshStandardMaterial({
        color: '#d6c39e',
        roughness: 0.78,
        metalness: 0,
        side: THREE.DoubleSide,
      }),
    ],
    // painted: 가구가 자기 텍스처로 덮어쓴다 (materials() 참고)
    ['painted', new THREE.MeshStandardMaterial({ color: '#cbb684', roughness: 0.9, side: THREE.DoubleSide })],
    ['iron', new THREE.MeshStandardMaterial({ color: '#403a34', roughness: 0.62, metalness: 0.72 })],
    // 옹기: 유약이 얇게 흘러 광택이 얼룩진다
    ['earthen', new THREE.MeshStandardMaterial({ color: '#4a3428', roughness: 0.46, metalness: 0.04 })],
    // 피 · 살은 절차 재질 (flesh.ts) — 표면하산란과 육즙 막이 들어간다
    ['blood', fleshMaterials().blood],
    ['flesh', fleshMaterials().flesh],
    // 위생도기 — 흰데 완전히 희지 않다. 오래 쓴 도기는 누렇게 뜬다
    ['porcelain', new THREE.MeshStandardMaterial({ color: '#e2e0d6', roughness: 0.22, metalness: 0.02 })],
    // 벽 조각 — 세계의 회벽과 같은 결을 쓴다
    ['plaster', wood(surf.get('plaster')!.material, '#9a9184', 0.92)],

    // --- 2026 년 ---
    // 사출 플라스틱: 금속기가 있으면 즉시 장난감으로 보인다. metalness 0
    ['plastic', new THREE.MeshStandardMaterial({ color: '#2c2e33', roughness: 0.64, metalness: 0 })],
    // 아노다이징 알루미늄: 강철(0.82)보다 금속기를 낮추고 거칠기를 올려야
    // 「깎아 만든 은색」이 아니라 「입힌 은색」으로 읽힌다
    ['alu', new THREE.MeshStandardMaterial({ color: '#a8adb4', roughness: 0.44, metalness: 0.58 })],
    ['cardboard', new THREE.MeshStandardMaterial({ color: '#b18e63', roughness: 0.97, metalness: 0 })],
    ['rubber', new THREE.MeshStandardMaterial({ color: '#1a1b1e', roughness: 0.88, metalness: 0 })],
    // 숯·재: `Slot` 타입에는 있는데 **여기 정의가 빠져 있었다** — 기본 흰 재질로 떨어져서
    // 연탄도 탄 종이도 하얗게 나왔다. 완전한 검정이 아니라 회끼 도는 숯이고 광택이 없다
    ['coal', new THREE.MeshStandardMaterial({ color: '#2b2724', roughness: 0.99, metalness: 0 })],

    // --- 건축 (docs/ARCHITECTURE_CATALOG.md) ---
    // 시멘트: 벽/바닥과 같은 절차 텍스처를 쓴다. 색만 얹으면 목공방 바닥이
    // 방 바탕과 다른 재질처럼 떠 보인다
    ['concrete', wood(surf.get('concrete')!.material, '#8d8b86', 0.94)],
    // 창유리: **transmission 을 쓰지 않는다.** transmission 이 있는 메시가 씬에 하나만
    // 있어도 three 는 프레임마다 불투명 씬을 별도 타깃에 다시 그린다. 창이 서른일곱이라
    // 그 비용이 상시로 든다. 대신 반사와 낮은 불투명도로 유리를 읽히게 한다 —
    // 밤이고 실내가 어두워서 창은 대개 반사면으로 보인다.
    [
      'pane',
      new THREE.MeshStandardMaterial({
        color: '#8fa6bb',
        roughness: 0.08,
        metalness: 0.14,
        transparent: true,
        opacity: 0.28,
        side: THREE.DoubleSide,
      }),
    ],
    // 간유리: 현관문 슬릿·욕실 문처럼 «안이 보이면 안 되는» 유리.
    // **투과도 반투명도 주지 마라.** glass(투과 0.9)로 뒀더니 한서진 집 현관 슬릿
    // 너머로 흰 덩어리가 보였고, 광선을 쏴 보니 그 집 «지붕에 쌓인 눈»이었다 —
    // 실내에 천장이 없어서 문 유리로 지붕 속이 그대로 비쳤다. 밤에 불 꺼진
    // 간유리는 어차피 뿌연 회색 반사면이다.
    ['frost', new THREE.MeshStandardMaterial({ color: '#7c848c', roughness: 0.55, metalness: 0.06 })],
    // 골슬레이트: 90 년대 시골 지붕. 금속기를 조금 남겨 비 맞은 티를 낸다
    ['slate', new THREE.MeshStandardMaterial({ color: '#5a5f63', roughness: 0.78, metalness: 0.18 })],
    // 건축 목부재: darkwood 를 **그대로** 쓴다. wood() 처럼 색·roughness 를 바꾸면
    // 벽과 다른 재질처럼 떠 보이고, roughness 를 낮추면 흰 점이 튄다.
    // vertexColors 만 켠다 (MeshWriter 가 color 속성을 넣기 때문에 필요하다).
    ['trim', (() => { const m = woodTex.material.clone(); m.vertexColors = true; return m; })()],
    // 촛불: 조명을 안 받고 스스로 빛난다. 광원은 따로 붙으므로 여기서는 **불꽃의 몸**만
    // 만든다. `emissiveIntensity` 는 `shrineSet.update` 가 매 프레임 흔든다
    [
      'flame',
      new THREE.MeshStandardMaterial({
        color: '#ffb14a',
        emissive: new THREE.Color('#ffa32e'),
        emissiveIntensity: 2.4,
        roughness: 1,
        metalness: 0,
        transparent: true,
        opacity: 0.92,
        depthWrite: false,
        // 불은 노출·톤매핑에 눌리면 안 된다. 켜 두지 않으면 어두운 방에서
        // 촛불이 그냥 누런 점으로 보인다
        toneMapped: false,
      }),
    ],
    // 화면: 가구가 자기 텍스처로 덮어쓴다. 기본값은 꺼진 검은 유리다 —
    // 텍스처를 안 준 화면은 켜져 있으면 안 된다
    [
      'screen',
      new THREE.MeshStandardMaterial({
        color: '#0a0b0d',
        roughness: 0.14,
        metalness: 0.1,
        emissive: new THREE.Color('#000000'),
      }),
    ],
  ]);
  for (const m of cache.values()) (m as THREE.MeshStandardMaterial).vertexColors = true;
  return cache;
}
